import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { customInstance } from "../../services/api-client";
import type {
  APInvoiceConnectorOption,
  APInvoiceDataSource,
  APInvoiceDataSourceConfigUpdate,
  APInvoiceHealth,
  APInvoiceHistoryResponse,
  APInvoiceSourceFolderIngestRequest,
  APInvoiceSourceFolderIngestResponse,
  APInvoiceUploadRequest,
} from "./types";
import { buildInvoiceUploadForm } from "./utils";

const BASE = "/api/ap-invoice-agent";

export const apInvoiceAgentKeys = {
  all: ["ap-invoice-agent"] as const,
  history: (limit: number) =>
    [...apInvoiceAgentKeys.all, "history", limit] as const,
  config: () => [...apInvoiceAgentKeys.all, "config"] as const,
  connectors: () =>
    [...apInvoiceAgentKeys.all, "available-connectors"] as const,
};

export function useAPInvoiceHistory(limit = 100) {
  return useQuery<APInvoiceHistoryResponse>({
    queryKey: apInvoiceAgentKeys.history(limit),
    queryFn: () =>
      customInstance({
        url: `${BASE}/history`,
        method: "GET",
        params: { limit },
      }),
    retry: false,
  });
}

export function useAPInvoiceDataSourceConfig() {
  return useQuery<APInvoiceDataSource>({
    queryKey: apInvoiceAgentKeys.config(),
    queryFn: () => customInstance({ url: `${BASE}/config`, method: "GET" }),
    staleTime: 60_000,
    retry: false,
  });
}

export function useAPInvoiceAvailableConnectors() {
  return useQuery<APInvoiceConnectorOption[]>({
    queryKey: apInvoiceAgentKeys.connectors(),
    queryFn: () =>
      customInstance({
        url: `${BASE}/config/available-connectors`,
        method: "GET",
      }),
    staleTime: 60_000,
  });
}

export function useSetAPInvoiceDataSource() {
  const queryClient = useQueryClient();
  return useMutation<
    APInvoiceDataSource,
    Error,
    APInvoiceDataSourceConfigUpdate
  >({
    mutationFn: (payload) =>
      customInstance({
        url: `${BASE}/config`,
        method: "PUT",
        data: payload,
      }),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: apInvoiceAgentKeys.all }),
  });
}

export function useClearAPInvoiceDataSource() {
  const queryClient = useQueryClient();
  return useMutation<APInvoiceDataSource, Error, void>({
    mutationFn: () =>
      customInstance({
        url: `${BASE}/config`,
        method: "DELETE",
      }),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: apInvoiceAgentKeys.all }),
  });
}

export function useVerifyAPInvoiceConnection() {
  return useMutation<APInvoiceHealth, Error, APInvoiceDataSourceConfigUpdate>({
    mutationFn: (payload) =>
      customInstance({
        url: `${BASE}/config/test-connection`,
        method: "POST",
        data: payload,
      }),
  });
}

export function useIngestSourceFolderAPInvoices() {
  const queryClient = useQueryClient();
  return useMutation<
    APInvoiceSourceFolderIngestResponse,
    Error,
    APInvoiceSourceFolderIngestRequest
  >({
    mutationFn: (payload) =>
      customInstance({
        url: `${BASE}/ingest/source-folder`,
        method: "POST",
        data: payload,
      }),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: apInvoiceAgentKeys.all }),
  });
}

export function useUploadAPInvoice() {
  const queryClient = useQueryClient();
  return useMutation<
    APInvoiceSourceFolderIngestResponse,
    Error,
    APInvoiceUploadRequest
  >({
    mutationFn: (payload) =>
      customInstance({
        url: `${BASE}/ingest/upload`,
        method: "POST",
        data: buildInvoiceUploadForm(payload),
      }),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: apInvoiceAgentKeys.all }),
  });
}
